import type { SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { isDpsScheme, isTwoStageProcedure } from "../../../sourcing-events/buyer/wizard/utils/procedure";

export type ChangeWindow = "open" | "reopenable" | "closed";

export type ChangedTerm = "dpsValidTo" | "bidSubmissionDeadline";

export const changedTermOf = (event: SourcingEventV2Dto): ChangedTerm =>
  isDpsScheme(publicContextOf(event)?.procedureType) ? "dpsValidTo" : "bidSubmissionDeadline";

// Mirrors `SourcingEventV2.changeWindowClosesAt` on the BFF — keep the two in step.
const changeWindowClosesAt = (event: SourcingEventV2Dto): string | null | undefined => {
  const publicContext = publicContextOf(event);
  if (isDpsScheme(publicContext?.procedureType)) return publicContext?.dpsScheme?.validTo;
  if (isTwoStageProcedure(publicContext?.procedureType)) return undefined;
  return event.milestoneDates?.bidSubmissionDeadline;
};

/** An FOA Part I procurement runs the private profile too, so "reopenable" covers both. */
export const changeWindowOf = (event: SourcingEventV2Dto, now: number = Date.now()): ChangeWindow => {
  const closesAt = changeWindowClosesAt(event);
  if (closesAt == null) return "closed";
  if (new Date(closesAt).getTime() > now) return "open";
  return event.eventContext.type === "Private" ? "reopenable" : "closed";
};

export interface DeadlineEditing {
  earliestSelectable: Date | undefined;
  canReopen: boolean;
  isClosed: boolean;
  warnsDeadlineIsNear: boolean;
}

const NEAR_DEADLINE_MS = 24 * 60 * 60 * 1000;

export const deadlineEditingOf = (event: SourcingEventV2Dto, now: number = Date.now()): DeadlineEditing => {
  const closesAt = changeWindowClosesAt(event);
  const window = changeWindowOf(event, now);
  const isAnnounced = event.eventContext.type !== "Private";
  return {
    earliestSelectable:
      window === "reopenable" ? new Date(now) : closesAt != null ? new Date(closesAt) : undefined,
    canReopen: window === "reopenable",
    isClosed: window === "closed",
    warnsDeadlineIsNear:
      window === "open" &&
      isAnnounced &&
      closesAt != null &&
      new Date(closesAt).getTime() - now < NEAR_DEADLINE_MS,
  };
};

const publicContextOf = (event: SourcingEventV2Dto) =>
  event.eventContext.type === "Public" ? event.eventContext : null;
